import React, { useRef, useState, useEffect } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import { ColorType, ILogoProps } from '@dealroadshow/uikit-logo/types';

import { Link, TabLink as NextNavLink } from '@framework/router/Next/Link';
import HeaderBanner from '@framework/ui/Organisms/HeaderBanner';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import isDefined from '@framework/data-helpers/isDefined';
import useElementSize from '@framework/hooks/useElementSize';
import IconHamburgerMenu from '@dealroadshow/uikit-icon/IconHamburgerMenu';
import IconClose from '@dealroadshow/uikit-icon/IconClose';

import interactionStyles from '@dealroadshow/uikit-styles/helpers/interaction.module.scss';
import styles from './header.module.scss';

interface IMenuItem {
  key: string,
  title: string,
  path: string,
  isActive?: () => boolean,
}

interface IHeaderButtonProps {
  buttonProps: {
    className: string,
    variant: string,
    onClick: () => void,
  },
}

interface ILoginButtonProps {
  buttonProps: {
    variant: string,
    className: string,
    title: string,
    dataTest: string,
  },
  linkProps: {
    dataTest: string,
  },
}

interface IProps {
  logoComponent: React.ComponentType<ILogoProps>,
  menu: IMenuItem[],
  loginUrl?: string,
  customLoginButton?: React.ComponentType<ILoginButtonProps>,
  headerButton?: React.ComponentType<IHeaderButtonProps>,
  logoClassName?: string,
  mobileMenuClassName: string,
  isWideMenu?: boolean,
}

const Header = ({
  logoComponent: LogoComponent,
  menu,
  customLoginButton: CustomLoginButton,
  headerButton: HeaderButton,
  loginUrl,
  logoClassName,
  mobileMenuClassName,
  isWideMenu = false,
}: IProps) => {
  const headerRef = useRef(null);
  const lastScrollPos = useRef(0);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const { height: headerHeight } = useElementSize('landingHeader');

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isMobileMenuOpened, setMobileMenuOpened] = useState(false);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Hide or show the menu.
  const handleScroll = throttle(() => {
    setIsHeaderFixed(window.scrollY > 0);
    // Fix for throttled scroll on redirect
    if (!headerRef.current) {
      return;
    }

    const currentScroll = document.documentElement.scrollTop;
    // @ts-ignore
    const isSticky = currentScroll > headerRef.current.clientHeight;

    if (isSticky) {
      if (currentScroll > 0 && lastScrollPos.current <= currentScroll) {
        lastScrollPos.current = currentScroll;
        setIsHeaderVisible(false);
      } else {
        lastScrollPos.current = currentScroll;
        setIsHeaderVisible(true);
      }
    } else {
      setIsHeaderVisible(true);
    }
  }, 100);

  const onResize = throttle(() => {
    handleMobileMenuToggle(false);
  }, 100);

  const handleMobileMenuToggle = (toggle?: boolean) => {
    toggle = isDefined(toggle) ? toggle : !isMobileMenuOpened;
    // @ts-ignore
    setMobileMenuOpened(toggle);
    // IE11 Fix
    if (toggle) {
      document.body.classList.add(interactionStyles.nonScrollable);
    } else {
      document.body.classList.remove(interactionStyles.nonScrollable);
    }
  };

  const closeMenu = () => handleMobileMenuToggle(false);

  const handleMobileMenuLink = () => {
    if (isMobileMenuOpened) {
      handleMobileMenuToggle(false);
    }
  };

  const renderLoginButton = () => {
    const linkProps: ILoginButtonProps['linkProps'] = {
      dataTest: 'headerLogIn',
    };

    const buttonProps: ILoginButtonProps['buttonProps'] = {
      variant: ButtonVariantType.outline,
      className: cn(styles.menuButton, styles.menuLoginButton),
      title: 'Log In',
      dataTest: 'logInButton',
    };

    if (CustomLoginButton) {
      return (
        <CustomLoginButton
          buttonProps={ buttonProps }
          linkProps={ linkProps }
        />
      );
    }

    if (loginUrl) {
      return (
        <Link
          to={ loginUrl }
          { ...linkProps }
        >
          <Button { ...buttonProps } />
        </Link>
      );
    }

    return null;
  };

  const renderHeaderButton = () => {
    if (!HeaderButton) {
      return null;
    }

    return (
      <HeaderButton
        buttonProps={ {
          className: cn(styles.menuButton, styles.menuCustomButton),
          variant: ButtonVariantType.action,
          onClick: closeMenu,
        } }
      />
    );
  };

  const renderActions = () => (
    <>
      { renderLoginButton() }
      { renderHeaderButton() }
    </>
  );

  const renderMenu = (menu: IMenuItem[]) => menu.map((menuItem) => (
    <NextNavLink
      key={ menuItem.key }
      to={ menuItem.path }
      title={ menuItem.title }
      dataTest={ `${ menuItem.key }NavLink` }
      className={ styles.menuLink }
      activeClassName={ styles.isMenuLinkActive }
      isExactActive
      { ...(menuItem?.isActive && { isActive: menuItem.isActive() }) }
      onClick={ handleMobileMenuLink }
    >
      { menuItem.title }
    </NextNavLink>
  ));

  const headerCls = cn(styles.animatedHeader, {
    [styles.isHeaderHidden]: !isHeaderVisible,
  });

  const menuClassName = cn(styles.mobileMenu, mobileMenuClassName, {
    [styles.menuOpened]: isMobileMenuOpened,
    [styles.wideMenuMobile]: isWideMenu,
  });

  const logoImgClassName = cn(styles.logoImage, logoClassName);

  return (
    <>
      { isMobileMenuOpened && <div className={ styles.headerOverlay } onClick={ closeMenu } /> }
      { isHeaderFixed && <div style={ { height: headerHeight } } /> }
      <div
        className={ cn(styles.header, {
          [styles.headerFixed]: isHeaderFixed,
        }) }
        id="landingHeader"
        data-test="header"
      >
        <HeaderBanner isSticky={ false } />
        <div
          className={ headerCls }
          ref={ headerRef }
        >
          <div className={ styles.headerWrp }>
            <div className={ cn(styles.headerContainer, { [styles.wideMenuContainer]: isWideMenu }) } data-test="desktopNav">
              <div>
                <div className={ styles.burger } onClick={ () => handleMobileMenuToggle() }>
                  { !isMobileMenuOpened ? <IconHamburgerMenu /> : <IconClose /> }
                </div>
                <div className={ styles.logo }>
                  <Link to="/" onClick={ closeMenu }>
                    <LogoComponent
                      colorType={ ColorType.DARK }
                      className={ logoImgClassName }
                    />
                  </Link>
                </div>
                <div className={ styles.divider } />
                <div className={ styles.menu }>
                  <div className={ styles.headerMenu }>{ renderMenu(menu) }</div>
                </div>
              </div>
              <div className={ styles.headerActions }>
                <div className={ styles.headerUserActions } data-test="headerUserActions">{ renderActions() }</div>
              </div>
            </div>
          </div>
          <div className={ menuClassName } data-test="mobileNav">
            <div>
              <div className={ styles.mobileHeaderMenu }>
                <div
                  className={ styles.closeMenuButton }
                  onClick={ closeMenu }
                >
                  <IconClose />
                </div>
                <div className={ styles.logo }>
                  <Link to="/" onClick={ closeMenu }>
                    <LogoComponent
                      colorType={ ColorType.WHITE }
                      className={ logoImgClassName }
                    />
                  </Link>
                </div>
              </div>
              <div>{ renderMenu(menu) }</div>
            </div>
            <div className={ styles.mobileActionsWrp }>{ renderActions() }</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
